/**
 * Protected Route Component
 * 
 * This component wraps routes that require authentication.
 * It checks if the user is authenticated before allowing access.
 * If not authenticated, it redirects to the login page.
 * 
 * Features:
 * - Authentication state checking
 * - Loading states for async operations
 * - Error handling
 * - Redirect to login on unauthenticated access
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated as checkIsAuthenticated } from '../utils/auth';

/**
 * Loading component for authentication check
 */
const AuthLoading = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-600 font-medium">Verifying authentication...</p>
    </div>
  </div>
);

/**
 * ProtectedRoute Component
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {JSX.Element} Either the children or a redirect to login
 */
const ProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const location = useLocation();

  /**
   * Check authentication status on component mount
   * This simulates an async authentication check
   */
  const checkAuth = useCallback(() => {
    try {
      // Small delay to simulate async operation
      setTimeout(() => {
        const authStatus = checkIsAuthenticated();
        setIsAuth(authStatus);
        setIsLoading(false);
      }, 100);
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuth(false);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Show loading state while checking authentication
  if (isLoading) {
    return <AuthLoading />;
  }

  // Redirect to login if not authenticated
  // Save the current location to redirect back after login
  if (!isAuth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return children;
};

export default ProtectedRoute;
